<template>
  <div class="global-map-wrapper">
    <template v-if="showFilters">
      <p class="title title--3 mb-2">{{ resolvedTitle }}</p>
      <div class="global-map-wrapper__legend legend">
      <button
        class="legend__item"
        :class="{ active: selectedDivisionId === null }"
        @click="selectedDivisionId = null"
      >
        <span class="legend__color" style="background-color: #13a538"></span>
        {{ allDivisionsLabel }}
      </button>

      <button
        v-for="division in divisions"
        :key="division.id"
        class="legend__item"
        :class="{ active: selectedDivisionId === division.id }"
        @click="selectedDivisionId = division.id"
      >
        <span
          class="legend__color"
          :style="{ backgroundColor: division.colour }"
        ></span>
        {{ division.name }}
      </button>
      </div>
    </template>

    <div id="global-map" class="global-map"></div>
  </div>
</template>

<script>
import { nextTick } from "vue";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// Fix default marker icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Cache for hex markers by color
const iconCache = {};
function createHexMarker(hexColor) {
  if (!iconCache[hexColor]) {
    const svg = encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 40">
        <path d="M12 0C5.372 0 0 5.372 0 12c0 9.6 12 28 12 28s12-18.4 12-28c0-6.628-5.372-12-12-12z" fill="${hexColor}"/>
        <circle cx="12" cy="12" r="5" fill="white"/>
      </svg>
    `);

    iconCache[hexColor] = L.icon({
      iconUrl: `data:image/svg+xml,${svg}`,
      iconSize: [24, 40],
      iconAnchor: [12, 40],
      popupAnchor: [0, -35],
    });
  }
  return iconCache[hexColor];
}

export default {
  name: "CompanyMap",
  props: {
    divisions: { type: Array, required: true },
    companies: { type: Array, required: true },
    allDivisionsLabel: { type: String, default: "All Divisions" },
    title: { type: String, default: "Filter by Division:" },
    disableClustering: { type: Boolean, default: false },
    cartoApiKey: { type: String, default: "" },
  },
  data() {
    return {
      map: null,
      markerClusterGroup: null,
      selectedDivisionId: null,
    };
  },
  computed: {
    // CARTO's basemaps need a key; without one the tiles come back stamped
    // "API key required". Left off entirely when no key is configured, rather
    // than sending an empty parameter.
    tileUrl() {
      const base = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

      return this.cartoApiKey
        ? `${base}?key=${encodeURIComponent(this.cartoApiKey)}`
        : base;
    },
    resolvedTitle() {
      return this.title && this.title.trim()
        ? this.title
        : "Filter by Division:";
    },
    showFilters() {
      return Array.isArray(this.divisions) && this.divisions.length > 1;
    },
    filteredCompanies() {
      if (this.selectedDivisionId === null) return this.companies;
      return this.companies.filter(
        (c) => c.divisionId === this.selectedDivisionId
      );
    },
  },
  watch: {
    selectedDivisionId() {
      this.updateMarkers();
    },
    companies: {
      handler() {
        this.updateMarkers();
      },
      deep: true,
    },
  },
  mounted() {
    nextTick(() => {
      if (document.getElementById("global-map")) {
        this.initMap();
      }
    });
  },
  methods: {
    initMap() {
      if (!document.getElementById("global-map")) return;

      this.map = L.map("global-map", {
        center: [54.5, -3],
        zoom: 6,
        zoomControl: false,
      });

      L.control.zoom({ position: "bottomright" }).addTo(this.map);

      L.tileLayer(
        this.tileUrl,
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
          minZoom: 1,
          maxZoom: 10,
        }
      ).addTo(this.map);

      if (this.disableClustering) {
        this.markerClusterGroup = L.featureGroup();
      } else {
        this.markerClusterGroup = L.markerClusterGroup({
          // Make clustering less aggressive so markers separate earlier.
          maxClusterRadius: 35,
          disableClusteringAtZoom: 7,
          showCoverageOnHover: false,
          spiderfyOnMaxZoom: true,
        });
      }
      this.map.addLayer(this.markerClusterGroup);

      this.updateMarkers();
    },

updateMarkers() {
  if (!this.map || !this.markerClusterGroup) return;

  // Temporarily disable MarkerCluster animation to avoid _panAnim errors.
  const hasAnimateOption = Object.prototype.hasOwnProperty.call(
    this.markerClusterGroup.options || {},
    "animate"
  );
  const oldAnimate = hasAnimateOption
    ? this.markerClusterGroup.options.animate
    : null;
  if (hasAnimateOption) {
    this.markerClusterGroup.options.animate = false;
  }

  // Clear existing markers
  this.markerClusterGroup.clearLayers();

  // Add filtered companies
  this.filteredCompanies.forEach((company) => {
    const division = this.divisions.find(d => d.id === company.divisionId);
    const color = division ? division.colour : "#13a538";

    const marker = L.marker([company.lat, company.lng], {
      icon: createHexMarker(color),
    });

    const popupContent = `
      <div class="global-map__pop-up">
        <img src="${company.logo}" alt="${company.name} Logo" />
        <p class="title title--5">${company.name}</p>
        <div class="richtext">
          ${division ? `<p><strong>Division:</strong> ${division.name}</p>` : ""}
          ${company.office ? `<p><strong>Office:</strong> ${company.office}</p>` : ""}
          <p><strong>Address:</strong> ${company.address}</p>
          ${company.googleMapsUrl ? `<p class="mt-1"><a href="${company.googleMapsUrl}" target="_blank" rel="noopener">View on Google Maps</a></p>` : ""}
        </div>
        ${company.website ? `<a class="button button--secondary button--small" href="${company.website}" target="_blank" rel="noopener">Visit Website</a>` : ""}
      </div>
    `;

    marker.bindPopup(popupContent, {
      keepInView: true,
      autoPan: true,
      autoPanPaddingTopLeft: [50, 75],
      autoPanPaddingBottomRight: [50, 50],
      maxWidth: 300,
    });

    this.markerClusterGroup.addLayer(marker);
  });

  // Safely fit bounds after cluster update
  const layers = this.markerClusterGroup.getLayers();
  if (layers.length > 0) {
    const bounds = this.markerClusterGroup.getBounds();
    if (bounds.isValid()) {
      try {
        requestAnimationFrame(() => {
          this.map.fitBounds(bounds.pad(0.2), { animate: false, maxZoom: 8 });
        });
      } catch (e) {
        console.warn("Leaflet fitBounds skipped due to invalid bounds", e);
      }
    }
  }

  // Restore MarkerCluster animation option when clustering is active.
  if (hasAnimateOption) {
    this.markerClusterGroup.options.animate = oldAnimate;
  }
},

  },
};
</script>
